/**
 * Run recordings, format version 3 (version 3 adds the crouch input; the Phase 4 developer recorder's plain
 * JSON is version 1 and stays as it is).
 *
 * A recording is everything needed to reproduce one attempt exactly with the
 * real simulation: which track and movement rules it ran under (the record
 * key carries the track's gameplay fingerprint and the movement configuration
 * hash), the simulation version, tick rate and respawn delay, the starting
 * mode and spawn, every tick's input (InputTape) and the few commands that are
 * not inputs (a practice restart to the last checkpoint). It also stores what
 * the run produced (time, splits, deaths, eligibility) and state hashes every
 * HASH_INTERVAL ticks plus at the end, so a replay can prove it reproduced the
 * run, and say roughly where it stopped doing so.
 *
 * The file is JSON with the input stream packed, deflated and base64-encoded.
 * `parseRecording` treats the text as hostile: it bounds the file size, every
 * string, count and number, caps decompression at the size the declared tick
 * count allows, and decodes the inputs completely before anything uses them.
 * A file that passes is well-formed; whether it is *compatible* (same track,
 * rules and simulation) is a separate question (`compatibility`), and whether
 * it is *authentic* is one this format cannot answer: see docs/REPLAYS.md.
 */
import type { GameMode } from '../gameplay/AttemptManager';
import { MAX_SPAWN_COORD } from '../simulation/Simulation';
import type { SpawnPoint } from '../world/LevelData';
import { decodeTape, deflate, encodeTape, fromBase64, inflate, maxEncodedBytes, TapeError, toBase64, type InputTape } from './InputTape';
import { INELIGIBLE_TEXT, type IneligibleReason, type Split } from './RunTiming';

export const RECORDING_FORMAT = 'xernous.run';
export const RECORDING_VERSION = 3;
/**
 * Identity of the simulation's behaviour. Bump it whenever a change could make
 * the same inputs produce a different run (motor, collision, triggers, timing,
 * respawn). Recordings from another simulation version are never treated as
 * authoritative.
 */
export const SIMULATION_VERSION = 'xernous-sim.9';
export const APP_VERSION = '0.1.0';
/** 20 minutes at 120 Hz. */
export const MAX_RUN_TICKS = 120 * 60 * 20;
/** A state hash is stored every this many ticks (and always at the end). */
export const HASH_INTERVAL = 600;
/** Largest recording file accepted (UTF-8 bytes). */
export const MAX_RECORDING_BYTES = 4 * 1024 * 1024;
const MAX_COMMANDS = 10_000;
const MAX_SPLITS = 128;
const MAX_TEXT = 200;

export type RunOutcome = 'finished' | 'incomplete';

export interface RunCommand {
  /** Simulation tick (relative to the recording's first tick) before which the command applies. */
  tick: number;
  kind: 'restart';
}

export interface RunResult {
  outcome: RunOutcome;
  /** Official run time (null when the run did not finish timed). */
  seconds: number | null;
  splits: Split[];
  deaths: number;
  /** Eligibility when it was recorded (empty: it counted as a record run). */
  ineligible: IneligibleReason[];
}

export interface RunRecording {
  format: typeof RECORDING_FORMAT;
  version: typeof RECORDING_VERSION;
  build: { simulation: string; app: string };
  track: { id: string; title: string; recordKey: string; ruleset: string };
  config: { tickRate: number; respawnDelayTicks: number };
  start: { mode: GameMode; spawn: SpawnPoint };
  ticks: number;
  inputs: InputTape;
  commands: RunCommand[];
  result: RunResult;
  /** State hashes after ticks HASH_INTERVAL, 2·HASH_INTERVAL, … (only full intervals). */
  hashes: string[];
  finalHash: string;
  meta: { name: string; createdAt: number; origin: 'local' | 'imported' };
}

export class RecordingFormatError extends Error {}

interface WireRecording extends Omit<RunRecording, 'inputs'> {
  inputs: string;
}

export async function serializeRecording(rec: RunRecording): Promise<string> {
  const packed = toBase64(await deflate(encodeTape(rec.inputs)));
  const wire: WireRecording = { ...rec, inputs: packed };
  return JSON.stringify(wire);
}

export interface CompatibilityTarget {
  recordKey: string;
  tickRate: number;
  /** The respawn delay this build plays with (omitted: not checked). */
  respawnDelayTicks?: number;
}

export interface Compatibility {
  /** Replaying it here reproduces the recorded run (same simulation, track, rules and tick rate). */
  ok: boolean;
  reasons: string[];
}

/** Whether a recording can be replayed authoritatively against this track and configuration. */
export function compatibility(rec: Pick<RunRecording, 'build' | 'track' | 'config'>, target: CompatibilityTarget): Compatibility {
  const reasons: string[] = [];
  if (rec.build.simulation !== SIMULATION_VERSION) reasons.push(`It was recorded with another simulation version (${rec.build.simulation}; this build runs ${SIMULATION_VERSION}).`);
  const [recTrack, recMove] = splitKey(rec.track.recordKey);
  const [tTrack, tMove] = splitKey(target.recordKey);
  if (recTrack !== tTrack) reasons.push('It was recorded on a different version of this track (the gameplay fingerprint differs).');
  if (recMove !== tMove) reasons.push('It was recorded with different movement rules or tuning.');
  if (rec.config.tickRate !== target.tickRate) reasons.push(`It was recorded at ${rec.config.tickRate} Hz, not ${target.tickRate} Hz.`);
  if (target.respawnDelayTicks !== undefined && rec.config.respawnDelayTicks !== target.respawnDelayTicks) reasons.push(`It was recorded with a ${rec.config.respawnDelayTicks}-tick respawn delay; the game uses ${target.respawnDelayTicks}.`);
  return { ok: reasons.length === 0, reasons };
}

function splitKey(key: string): [string, string] {
  const i = key.indexOf('#');
  return i < 0 ? [key, ''] : [key.slice(0, i), key.slice(i + 1)];
}

export function utf8Bytes(text: string): number {
  return new TextEncoder().encode(text).byteLength;
}

/** Parses and fully validates an untrusted recording file. Throws RecordingFormatError with a readable reason. */
export async function parseRecording(text: string, maxBytes = MAX_RECORDING_BYTES): Promise<RunRecording> {
  if (text.length > maxBytes || utf8Bytes(text) > maxBytes) throw new RecordingFormatError(`The file is larger than ${Math.round(maxBytes / 1024)} KB.`);
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new RecordingFormatError('The file is not valid JSON.');
  }
  const v = new Validator();
  const o = v.object(raw, 'recording');
  if (o['format'] !== RECORDING_FORMAT) throw new RecordingFormatError('This is not a Xernous run recording.');
  if (o['version'] !== RECORDING_VERSION) throw new RecordingFormatError(`Unsupported recording version ${String(o['version'])} (this build reads version ${RECORDING_VERSION}).`);
  const build = v.object(o['build'], 'build');
  const track = v.object(o['track'], 'track');
  const config = v.object(o['config'], 'config');
  const start = v.object(o['start'], 'start');
  const result = v.object(o['result'], 'result');
  const meta = v.object(o['meta'], 'meta');
  const ticks = v.int(o['ticks'], 'ticks', 1, MAX_RUN_TICKS);
  const tickRate = v.int(config['tickRate'], 'tick rate', 30, 1000);
  const spawnRaw = v.object(start['spawn'], 'spawn');
  const pos = spawnRaw['position'];
  if (!Array.isArray(pos) || pos.length !== 3) throw new RecordingFormatError('The spawn position is invalid.');
  const spawn: SpawnPoint = { position: [0, 1, 2].map((i) => v.num(pos[i], 'spawn position', -MAX_SPAWN_COORD, MAX_SPAWN_COORD)) as [number, number, number], yawDeg: v.num(spawnRaw['yawDeg'], 'spawn yaw', -1e6, 1e6) };
  const commandsRaw = v.array(o['commands'], 'commands', MAX_COMMANDS);
  let lastTick = 0;
  const commands: RunCommand[] = commandsRaw.map((c) => {
    const co = v.object(c, 'command');
    if (co['kind'] !== 'restart') throw new RecordingFormatError('The recording contains an unknown command.');
    const tick = v.int(co['tick'], 'command tick', lastTick, ticks - 1);
    lastTick = tick;
    return { tick, kind: 'restart' };
  });
  const outcome = v.oneOf(result['outcome'], 'outcome', ['finished', 'incomplete'] as const);
  const maxSeconds = ticks / tickRate + 1;
  const seconds = result['seconds'] === null ? null : v.num(result['seconds'], 'run time', 0, maxSeconds);
  if (outcome === 'finished' && seconds === null) throw new RecordingFormatError('A finished recording has no run time.');
  const splits = v.array(result['splits'], 'splits', MAX_SPLITS).map((s) => {
    const so = v.object(s, 'split');
    return { index: v.int(so['index'], 'checkpoint index', 0, 999), time: v.num(so['time'], 'split time', 0, maxSeconds) };
  });
  const reasons = Object.keys(INELIGIBLE_TEXT) as IneligibleReason[];
  const ineligible = v.array(result['ineligible'], 'eligibility', reasons.length).map((r) => v.oneOf(r, 'eligibility reason', reasons));
  const hashes = v.array(o['hashes'], 'hashes', Math.floor(ticks / HASH_INTERVAL)).map((h) => v.hash(h, 'state hash'));
  if (hashes.length !== Math.floor(ticks / HASH_INTERVAL)) throw new RecordingFormatError('The recording has the wrong number of state hashes.');
  const packed = v.str(o['inputs'], 'inputs', Math.ceil((maxEncodedBytes(ticks) * 4) / 3) + 8);
  let inputs: InputTape;
  try {
    const bytes = await inflate(fromBase64(packed), maxEncodedBytes(ticks));
    inputs = decodeTape(bytes, ticks);
  } catch (err: unknown) {
    if (err instanceof TapeError) throw new RecordingFormatError(`The input data is damaged: ${err.message}.`);
    throw err;
  }
  return {
    format: RECORDING_FORMAT,
    version: RECORDING_VERSION,
    build: { simulation: v.str(build['simulation'], 'simulation version', 64), app: v.str(build['app'], 'app version', 64) },
    track: { id: v.str(track['id'], 'track id', MAX_TEXT), title: v.str(track['title'], 'track title', MAX_TEXT), recordKey: v.str(track['recordKey'], 'record key', 300), ruleset: v.str(track['ruleset'], 'ruleset', 64) },
    config: { tickRate, respawnDelayTicks: v.int(config['respawnDelayTicks'], 'respawn delay', 0, 1200) },
    start: { mode: v.oneOf(start['mode'], 'mode', ['practice', 'full-run'] as const), spawn },
    ticks,
    inputs,
    commands,
    result: { outcome, seconds, splits, deaths: v.int(result['deaths'], 'deaths', 0, ticks), ineligible },
    hashes,
    finalHash: v.hash(o['finalHash'], 'final hash'),
    meta: { name: v.str(meta['name'], 'name', 120), createdAt: v.num(meta['createdAt'], 'date', 0, 8.64e15), origin: v.oneOf(meta['origin'], 'origin', ['local', 'imported'] as const) },
  };
}

class Validator {
  object(x: unknown, what: string): Record<string, unknown> {
    if (typeof x !== 'object' || x === null || Array.isArray(x)) throw new RecordingFormatError(`The recording's ${what} is missing or not an object.`);
    return x as Record<string, unknown>;
  }

  array(x: unknown, what: string, max: number): unknown[] {
    if (!Array.isArray(x)) throw new RecordingFormatError(`The recording's ${what} is missing.`);
    if (x.length > max) throw new RecordingFormatError(`The recording has too many ${what} (${x.length}; at most ${max}).`);
    return x;
  }

  num(x: unknown, what: string, min: number, max: number): number {
    if (typeof x !== 'number' || !Number.isFinite(x) || x < min || x > max) throw new RecordingFormatError(`The recording's ${what} is out of range.`);
    return x;
  }

  int(x: unknown, what: string, min: number, max: number): number {
    const n = this.num(x, what, min, max);
    if (!Number.isInteger(n)) throw new RecordingFormatError(`The recording's ${what} is not a whole number.`);
    return n;
  }

  str(x: unknown, what: string, max: number): string {
    if (typeof x !== 'string') throw new RecordingFormatError(`The recording's ${what} is missing.`);
    if (x.length > max) throw new RecordingFormatError(`The recording's ${what} is too long.`);
    return x;
  }

  hash(x: unknown, what: string): string {
    const s = this.str(x, what, 16);
    if (!/^[0-9a-f]{16}$/.test(s)) throw new RecordingFormatError(`The recording's ${what} is malformed.`);
    return s;
  }

  oneOf<T extends string>(x: unknown, what: string, allowed: readonly T[]): T {
    if (typeof x !== 'string' || !(allowed as readonly string[]).includes(x)) throw new RecordingFormatError(`The recording's ${what} is not recognised.`);
    return x as T;
  }
}
